import React, { useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  IconButton,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import StarIcon from "@mui/icons-material/Star";

interface AccordionPanelProps {
  configs: any[];
  expandedConfigId: string | null;
  selectedSection: string;
  onAccordionChange: (configId: string) => void;
  onSectionSelect: (section: string) => void;
  onSystemInfoSelect: () => void;
  onNewSystem?: () => void;
  onNewConfig?: () => void;
}

const tabOptions = [
  "assetProcessing",
  "assetSyndication",
  "assetStorage",
  "clientProcess",
];

const AccordionPanel: React.FC<AccordionPanelProps> = ({
  configs,
  expandedConfigId,
  selectedSection,
  onAccordionChange,
  onSectionSelect,
  onSystemInfoSelect,
  onNewSystem,
  onNewConfig,
}) => {
  const theme = useTheme();
  const [systemExpanded, setSystemExpanded] = useState(true);
  const [configExpanded, setConfigExpanded] = useState(true);

  return (
    <Box
      sx={{
        width: "300px",
        borderRight: `1px solid ${theme.palette.divider}`,
        overflowY: "auto",
      }}
    >
      {/* 🔹 SYSTEM SECTION */}
      <Accordion
        expanded={systemExpanded}
        onChange={() => setSystemExpanded((prev) => !prev)}
        disableGutters
        square
        sx={{
          backgroundColor: theme.palette.background.paper,
          boxShadow: "none",
          "&:before": { display: "none" },
          m: 0,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            "& .MuiAccordionSummary-content": { display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" },
          }}
        >
          <Typography variant="h6">System</Typography>

          {/* ➕ Add System Icon */}
          {onNewSystem && (
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation(); // prevent toggling accordion
                onNewSystem();
              }}
              sx={{
                ml: 1,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          )}
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          <List disablePadding>
            <ListItemButton
              selected={selectedSection === "systemInfo"}
              onClick={onSystemInfoSelect}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {selectedSection === "systemInfo" && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.primary.main,
                          mr: 1,
                        }}
                      />
                    )}
                    <Typography variant="body2">System Info</Typography>
                  </Box>
                }
              />
            </ListItemButton>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* 🔹 CONFIGURATIONS SECTION */}
      <Accordion
        expanded={configExpanded}
        onChange={() => setConfigExpanded((prev) => !prev)}
        disableGutters
        square
        sx={{
          backgroundColor: theme.palette.background.paper,
          boxShadow: "none",
          "&:before": { display: "none" },
          m: 0,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            "& .MuiAccordionSummary-content": { display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" },
          }}
        >
          <Typography variant="h6">Configurations</Typography>

          {/* ➕ Add Config Icon (future) */}
          {onNewConfig && (
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                onNewConfig();
              }}
              sx={{
                ml: 1,
                "&:hover": { backgroundColor: theme.palette.action.hover },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          )}
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          {configs.map((cfg: any) => {
            const isDefault = cfg.clientProcess?.is_default || cfg.is_default;

            return (
              <Accordion
                key={cfg.name}
                expanded={expandedConfigId === cfg.name}
                onChange={() => onAccordionChange(cfg.name)}
                disableGutters
                square
                sx={{
                  backgroundColor:
                    expandedConfigId === cfg.name
                      ? theme.palette.action.hover
                      : "inherit",
                  boxShadow: "none",
                  "&:before": { display: "none" },
                  borderLeft:
                    expandedConfigId === cfg.name
                      ? `2px solid ${theme.palette.primary.main}`
                      : "none",
                  m: 0,
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Typography noWrap>
                      {cfg.clientProcess?.name || cfg.name}{" "}
                      {isDefault && (
                        <StarIcon color="error" fontSize="inherit" />
                      )}
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0 }}>
                  <List disablePadding>
                    {tabOptions.map((section) => (
                      <ListItemButton
                        key={section}
                        selected={
                          expandedConfigId === cfg.name &&
                          selectedSection === section
                        }
                        onClick={() => onSectionSelect(section)}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              {expandedConfigId === cfg.name &&
                                selectedSection === section && (
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      backgroundColor:
                                        theme.palette.primary.main,
                                      mr: 1,
                                    }}
                                  />
                                )}
                              <Typography variant="body2">{section}</Typography>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            );
          })
          }
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default AccordionPanel;
